"use client";

import { useCallback, useSyncExternalStore } from "react";

export type BooleanSettingOptions = {
  /** localStorage key used for persistence + cross-tab sync. */
  storageKey: string;
  /** CustomEvent name used for same-page sync across components. */
  eventName: string;
  /** Value used before hydration and when storage is unavailable. */
  defaultValue: boolean;
};

export type BooleanSetting = {
  /** React hook: `[value, setValue]`, backed by a shared external store. */
  useValue: () => readonly [boolean, (next: boolean) => void];
  /** Imperative read of the current persisted value. */
  read: () => boolean;
  /** Imperative write (persists + notifies same-page + other tabs). */
  write: (next: boolean) => void;
};

function parseEnabled(raw: string | null, defaultValue: boolean): boolean {
  if (raw === null) return defaultValue;
  const v = String(raw).trim().toLowerCase();
  return v === "true" || v === "1" || v === "yes" || v === "on";
}

/**
 * Factory for a boolean preference that is:
 * - persisted in localStorage,
 * - synced within the same page via a CustomEvent,
 * - synced across tabs via the native `storage` event.
 *
 * Unlike a per-component `useState + useEffect` hook, all consumers share a
 * single module-level store and a single pair of window listeners (attached on
 * the first subscriber, detached after the last unsubscribes). This keeps the
 * cost flat even when the hook is consumed by hundreds of nodes on the canvas.
 */
export function createBooleanSetting({
  storageKey,
  eventName,
  defaultValue,
}: BooleanSettingOptions): BooleanSetting {
  let current = defaultValue;
  const listeners = new Set<() => void>();

  const read = (): boolean => {
    try {
      return parseEnabled(window.localStorage.getItem(storageKey), defaultValue);
    } catch {
      return defaultValue;
    }
  };

  const notify = (): void => {
    for (const fn of listeners) {
      try {
        fn();
      } catch {}
    }
  };

  const setCurrent = (next: boolean): void => {
    if (next === current) return;
    current = next;
    notify();
  };

  const onCustom = (e: Event): void => {
    const detail = (e as CustomEvent<{ enabled?: boolean }>)?.detail;
    if (typeof detail?.enabled === "boolean") {
      setCurrent(detail.enabled);
      return;
    }
    setCurrent(read());
  };

  const onStorage = (e: StorageEvent): void => {
    if (e.key && e.key !== storageKey) return;
    setCurrent(read());
  };

  let attached = false;
  const subscribe = (cb: () => void): (() => void) => {
    const wasEmpty = listeners.size === 0;
    listeners.add(cb);
    if (wasEmpty && !attached) {
      attached = true;
      try {
        window.addEventListener(eventName, onCustom as EventListener);
      } catch {}
      try {
        window.addEventListener("storage", onStorage);
      } catch {}
      // (Re)sync from storage on attach; covers changes made by other tabs
      // while this page had no active subscribers.
      setCurrent(read());
    }
    return () => {
      listeners.delete(cb);
      if (listeners.size === 0 && attached) {
        attached = false;
        try {
          window.removeEventListener(eventName, onCustom as EventListener);
        } catch {}
        try {
          window.removeEventListener("storage", onStorage);
        } catch {}
      }
    };
  };

  const getSnapshot = (): boolean => current;
  const getServerSnapshot = (): boolean => defaultValue;

  const write = (next: boolean): void => {
    const v = Boolean(next);
    setCurrent(v);
    try {
      window.localStorage.setItem(storageKey, String(v));
    } catch {}
    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail: { enabled: v } }));
    } catch {}
  };

  const useValue = (): readonly [boolean, (next: boolean) => void] => {
    const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const setValue = useCallback((next: boolean) => write(next), []);
    return [value, setValue] as const;
  };

  return { useValue, read, write };
}
